import { FixFindingDrawer } from '../../components/drawers/fix-finding-drawer.tsx'
import { ScaFindingDrawer } from '../../components/drawers/sca-finding-drawer.tsx'
import { TriageFindingDrawer } from '../../components/drawers/triage-finding-drawer.tsx'
import { FindingsTabHeader } from '../../components/findings-tab-header.tsx'
import { useRepositoryActionMenuStore } from '../../components/menus/repository-action-menu.store.tsx'
import { ScanFindingsTableControls } from '../../components/scan-findings-table-controls.tsx'
import { Spinner } from '../../components/spinner.tsx'
import { FilterStateStoreProvider } from '../../components/tables/filter-state-store.tsx'
import { getFindingQuickViewConfig } from '../../components/tables/finding-quick-view-config'
import { ScanFindingsTable } from '../../components/tables/scan-findings-table.tsx'
import {
  useFindingQuickViewState,
  useGetScanLifecycleState,
  useSwitchToAnalyzedOnFirstBatchComplete,
} from '../../hooks/scan-hooks.ts'
import { useGetScans } from '../../utils/api-client/api-hooks.ts'
import { type ScanResponse } from '../../utils/api-client/api-schemas.ts'
import { useRedirectFromOldDrawerQueryParams } from '../../utils/hooks/use-redirect-from-old-drawer-query-params.ts'
import {
  emptyState,
  emptyStateAction,
  emptyStateDescription,
  emptyStateOr,
  emptyStateTitle,
} from './findings-tab.css.ts'
import { useRepositoryDetailsPageParams, useSelectedTool } from './repository-details-page.tsx'

export const FindingsTabWithProviders: React.FC = () => {
  const { repositoryId } = useRepositoryDetailsPageParams()
  const { selectedTool } = useSelectedTool()
  const toolsFilter = selectedTool ? [selectedTool] : undefined

  const { data: allScans, isLoading: isLoadingAll } = useGetScans({
    page: { number: 0, size: 1 },
    repositoryIds: [repositoryId],
    tools: toolsFilter,
  })
  const { data: analyzedScans, isLoading: isLoadingAnalyzed } = useGetScans({
    page: { number: 0, size: 1 },
    repositoryIds: [repositoryId],
    tools: toolsFilter,
    analysisState: 'completed',
  })

  const setCurrentOverlay = useRepositoryActionMenuStore(state => state.setCurrentOverlay)

  const isLoading = isLoadingAll || isLoadingAnalyzed
  const latestScan = allScans?._embedded?.items?.[0]
  const latestAnalyzedScan = analyzedScans?._embedded?.items?.[0]

  return (
    <>
      <FindingsTabRouter
        isLoading={isLoading}
        scan={latestScan}
        analyzedScan={latestAnalyzedScan}
        selectedTool={selectedTool ?? undefined}
        onCreateWorkflow={() => setCurrentOverlay('new-workflow')}
        onUploadScan={() => setCurrentOverlay('add-findings')}
        onMapTool={() => setCurrentOverlay('repository-settings')}
      />
    </>
  )
}

export const FindingsTabRouter: React.FC<{
  isLoading: boolean
  scan?: Pick<ScanResponse, 'id'>
  analyzedScan?: Pick<ScanResponse, 'id'>
  selectedTool?: string
  onCreateWorkflow: () => unknown
  onUploadScan: () => unknown
  onMapTool: () => unknown
}> = ({ isLoading, scan, analyzedScan, selectedTool, onCreateWorkflow, onUploadScan, onMapTool }) => {
  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          minHeight: '184px',
        }}
      >
        <Spinner />
      </div>
    )
  }

  if (!scan && !selectedTool) {
    return (
      <div className={emptyState}>
        <p className={emptyStateTitle}>No sources detected</p>
        <p className={emptyStateDescription}>
          No integrations or scans with tool results exist.{' '}
          <button className={emptyStateAction} onClick={onMapTool}>
            Map a tool to this repository
          </button>
          {'\n'}
          <span className={emptyStateOr}>OR</span>
          {'\n'}
          <button className={emptyStateAction} onClick={onUploadScan}>
            Upload a scan
          </button>{' '}
          to analyze findings now
        </p>
      </div>
    )
  }

  if (!scan) {
    return (
      <div className={emptyState}>
        <p className={emptyStateTitle}>No tool results</p>
        <p className={emptyStateDescription}>
          <button className={emptyStateAction} onClick={onCreateWorkflow}>
            Create a workflow
          </button>{' '}
          to set up automated analysis for this tool
          {'\n'}
          <span className={emptyStateOr}>OR</span>
          {'\n'}
          <button className={emptyStateAction} onClick={onUploadScan}>
            Upload a scan
          </button>{' '}
          to analyze findings now
        </p>
      </div>
    )
  }

  return <FindingsTabInner scanId={analyzedScan?.id ?? scan.id} />
}

const FindingsTabInner: React.FC<{ scanId: string }> = ({ scanId }) => {
  const { selectedTool } = useSelectedTool()
  const { selectedQuickView } = useFindingQuickViewState({ scanId })
  const { scanLifecycleState } = useGetScanLifecycleState({ scanId })
  useSwitchToAnalyzedOnFirstBatchComplete({ scanId })

  const isInitialOrFirstBatch =
    scanLifecycleState?.type === 'initial-unanalyzed-scan' ||
    scanLifecycleState?.type === 'first-analysis-batch-in-progress'
  const effectiveQuickView = isInitialOrFirstBatch ? ('all-findings' as const) : selectedQuickView
  const quickViewConfig = getFindingQuickViewConfig(effectiveQuickView)
  const storageKey = selectedTool ? `filter-state/${selectedTool}/${effectiveQuickView}` : undefined

  return (
    <FilterStateStoreProvider
      key={effectiveQuickView}
      initialState={quickViewConfig.defaultFilterState}
      storageKey={storageKey}
    >
      <FindingsTab scanId={scanId} />
    </FilterStateStoreProvider>
  )
}

const FindingsTab: React.FC<{ scanId: string }> = ({ scanId }) => {
  useRedirectFromOldDrawerQueryParams()
  const { repositoryId } = useRepositoryDetailsPageParams()
  const { selectedTool } = useSelectedTool()

  return (
    <section
      aria-label="Most recent analyzed findings"
      style={{ display: 'flex', flexDirection: 'column', gap: '32px', margin: '0 8px' }}
    >
      <FindingsTabHeader scanId={scanId} repositoryId={repositoryId} tool={selectedTool ?? undefined} />
      <ScanFindingsTableControls scanId={scanId} />
      <ScanFindingsTable scanId={scanId} />
      <TriageFindingDrawer scanId={scanId} />
      <ScaFindingDrawer scanId={scanId} />
      <FixFindingDrawer scanId={scanId} />
    </section>
  )
}
